import * as tradeInSelectors from '../store/mmd/tradeIn';
import clonedeep from 'lodash.clonedeep';

// types
import { StepperRoute, SelectedOption, UniqueOption, TradeInAssertionSelectors, IConfigTradeIn } from '../types';
import { TRADEIN_ESTIMATE_METHODS, TRADEIN_SOURCE_TYPES } from '../improvedWorkflow/components/constants';

// constants
import * as routes from './routes';

// actions
import * as tradeInComponentActionCreators from '../store/actionCreators';
import { DECISION_TYPES } from '../store/actionCreators';
import { getGlobalSelector } from '../store/mmd/mmd';
import { decisionOptions, upperCase } from '../improvedWorkflow/components/decisionPage/TradeInDecisionUI';
import { ITradeInState } from '@makemydeal/dr-activities-common';
import { isShowroomExperience } from './configUtils';

export enum tradeInMethod {
    ICO,
    STANDARD,
    MANUAL_ENTRY,
    GTP
}

enum tradeInDecisionOptions {
    submitForAppraisal = 'dealerAppraisal',
    kbbEstimate = 'kbbGuide',
    kbbICO = 'kbbIco',
    subaruGTP = 'subaruGTP'
}

export const getTradeInSelectedOption = (option: string) => {
    switch (option) {
        case tradeInDecisionOptions.subaruGTP:
        case TRADEIN_SOURCE_TYPES.subarugtp:
            return upperCase(DECISION_TYPES.SUBARU_GTP);
        case tradeInDecisionOptions.submitForAppraisal:
        case TRADEIN_SOURCE_TYPES.manual:
        case TRADEIN_ESTIMATE_METHODS.USER:
            return upperCase(DECISION_TYPES.ENTER_OWN_VALUE);
        case tradeInDecisionOptions.kbbICO:
        case TRADEIN_SOURCE_TYPES.ico:
            return upperCase(DECISION_TYPES.ICO);
        case tradeInDecisionOptions.kbbEstimate:
        case TRADEIN_SOURCE_TYPES.kbb:
        default:
            return upperCase(DECISION_TYPES.TRADE_IN_VALUE);
    }
};

const findSelectedOptionIndex = (option: string): number | undefined => {
    return decisionOptions.find((item: any) => item.label === option)?.index;
};

const getSelectedIndexAndOption = (
    optionsToShow: number[],
    tradeInSourceOption: string,
    providerOption: string
): SelectedOption => {
    const selectedProviderOption = getTradeInSelectedOption(providerOption);
    const selectedProviderOptionIndex = findSelectedOptionIndex(selectedProviderOption);
    const selectedTradeInSourceOption = tradeInSourceOption ? getTradeInSelectedOption(tradeInSourceOption) : '';
    const selectedTradeInSourceOptionIndex = findSelectedOptionIndex(selectedTradeInSourceOption);
    const isTradeInSourceOptionValid = optionsToShow.some((item) => item === selectedTradeInSourceOptionIndex);

    return isTradeInSourceOptionValid
        ? { selectedOptionIndex: selectedTradeInSourceOptionIndex, selectedOptionValue: selectedTradeInSourceOption }
        : { selectedOptionIndex: selectedProviderOptionIndex, selectedOptionValue: selectedProviderOption };
};

export const getAmdOptionsToShow = (selectors: TradeInAssertionSelectors): tradeInMethod[] => {
    const optionsToShow = [];

    if (selectors.isTradeInValuationProviderKBBICO) optionsToShow.push(tradeInMethod.ICO);
    if (selectors.isTradeInValuationProviderKBB) optionsToShow.push(tradeInMethod.STANDARD);
    if (selectors.isTradeInValuationProviderManual) optionsToShow.push(tradeInMethod.MANUAL_ENTRY);
    if (selectors.isTradeInValuationProviderGTP && optionsToShow.length !== 1) optionsToShow.unshift(tradeInMethod.GTP);

    return optionsToShow;
};

export const getAmdSelectedOption = (
    state: any,
    optionsToShow: tradeInMethod[],
    isTradeInValuationProviderGTP: boolean
): SelectedOption => {
    const defaultProviderOption = isTradeInValuationProviderGTP
        ? tradeInDecisionOptions.subaruGTP
        : tradeInSelectors.tradeInValuationProviderDefault(state);
    const tradeInSourceOption = getGlobalSelector().additionalSelectors.tradeInSelectors.getTradeInSource(state);

    return getSelectedIndexAndOption(optionsToShow, tradeInSourceOption, defaultProviderOption);
};

export const getAmdSelectors = (state: any): TradeInAssertionSelectors => {
    const isTradeInValuationProviderKBB = tradeInSelectors.isTradeInValuationProviderKBB(state);
    const isTradeInValuationProviderKBBICO = tradeInSelectors.isTradeInValuationProviderKBBICO(state);
    const isTradeInValuationProviderManual = tradeInSelectors.isTradeInValuationProviderManual(state);
    const isTradeInValuationProviderGTP = tradeInSelectors.isSubaruGtpEligible(state);

    return {
        isTradeInValuationProviderKBB,
        isTradeInValuationProviderKBBICO,
        isTradeInValuationProviderManual,
        isTradeInValuationProviderGTP
    };
};

export const getSalesViewOptionsToShow = (selectors: TradeInAssertionSelectors): tradeInMethod[] => {
    const optionsToShow = [];

    if (selectors.isTradeInValuationProviderKBBICOSalesView) optionsToShow.push(tradeInMethod.ICO);
    if (selectors.isTradeInValuationProviderKBBSalesView) optionsToShow.push(tradeInMethod.STANDARD);
    if (selectors.isTradeInValuationProviderManualSalesView) optionsToShow.push(tradeInMethod.MANUAL_ENTRY);
    if (selectors.isTradeInValuationProviderGTP && optionsToShow.length !== 1) optionsToShow.unshift(tradeInMethod.GTP);

    return optionsToShow;
};

export const getSalesViewSelectedOption = (
    state: any,
    optionsToShow: tradeInMethod[],
    isTradeInValuationProviderGTP: boolean
): SelectedOption => {
    const defaultProviderSalesViewOption = isTradeInValuationProviderGTP
        ? tradeInDecisionOptions.subaruGTP
        : tradeInSelectors.tradeInValuationProviderDefaultSalesView(state);
    const tradeInSourceOption = getGlobalSelector().additionalSelectors.tradeInSelectors.getTradeInSource(state);

    return getSelectedIndexAndOption(optionsToShow, tradeInSourceOption, defaultProviderSalesViewOption);
};

export const getSalesViewSelectors = (state: any): TradeInAssertionSelectors => {
    const isTradeInValuationProviderKBBSalesView = tradeInSelectors.isTradeInValuationProviderKBBSalesView(state);
    const isTradeInValuationProviderKBBICOSalesView = tradeInSelectors.isTradeInValuationProviderKBBICOSalesView(state);
    const isTradeInValuationProviderManualSalesView = tradeInSelectors.isTradeInValuationProviderManualSalesView(state);
    const { tradeInState } = tradeInSelectors.getTradeInState(state);
    const isTradeInValuationProviderGTP = tradeInSelectors.isSubaruGtpEligible(tradeInState);
    const isShowAppraisalValueScreenSalesView = tradeInSelectors.isShowAppraisalValueScreenSalesView(state);

    return {
        isTradeInValuationProviderKBBSalesView,
        isTradeInValuationProviderKBBICOSalesView,
        isTradeInValuationProviderManualSalesView,
        isTradeInValuationProviderGTP,
        isShowAppraisalValueScreenSalesView
    };
};

export const getTradeInSelectors = (state: any, isSalesView: boolean): TradeInAssertionSelectors => {
    return isSalesView ? getSalesViewSelectors(state) : getAmdSelectors(state);
};

export const getOptionsToShow = (tradeInSelectors: TradeInAssertionSelectors, isSalesView: boolean): tradeInMethod[] => {
    return isSalesView ? getSalesViewOptionsToShow(tradeInSelectors) : getAmdOptionsToShow(tradeInSelectors);
};

export const isLoanBalanceTriggerPoint = (tradeIn: ITradeInState, config: IConfigTradeIn, state: any): boolean => {
    const currentLocation = tradeIn.currentUserLocationRoute;

    const selectedEstimateMethodOption = tradeIn.source;
    const isEstimateAvailable =
        selectedEstimateMethodOption === TRADEIN_ESTIMATE_METHODS.SD ||
        selectedEstimateMethodOption === TRADEIN_ESTIMATE_METHODS.kbb;
    const isAppraisalAvailable = selectedEstimateMethodOption === TRADEIN_ESTIMATE_METHODS.USER;
    const isIcoAvailable = selectedEstimateMethodOption === TRADEIN_SOURCE_TYPES.ico;
    const isSubaruGtpAvailable = selectedEstimateMethodOption === TRADEIN_SOURCE_TYPES.subarugtp;
    const { routeSelectors } = getGlobalSelector().additionalSelectors;
    const isSalesView = routeSelectors.isDealerShowroomExperience(state);
    const tradeInAssertionSelectors = getTradeInSelectors(state, isSalesView);
    const isAppraisalValueScreenHidden = isSalesView && !tradeInAssertionSelectors.isShowAppraisalValueScreenSalesView;
    const uniqueEstimateOption = getUniqueEstimateOption(tradeInAssertionSelectors, isSalesView);
    const isSubaruGtpUniqueOptionAtVehicleInfoPage =
        isSubaruGtpAvailable && uniqueEstimateOption.hasUniqueOption && currentLocation === routes.TRADE_VEHICLE_INFO;

    // Estimate flow -> Equipment Options page
    if (isEstimateAvailable && currentLocation === routes.TRADE_EQUIPMENT_OPTIONS) return true;

    // Appraisal or ICO flows -> Details page
    if (
        (isAppraisalAvailable && currentLocation === routes.TRADE_MANUAL_ENTRY) ||
        (isIcoAvailable && currentLocation === routes.TRADE_IN_ICO) ||
        (isSubaruGtpAvailable && currentLocation === routes.TRADE_IN_DECISION) ||
        isSubaruGtpUniqueOptionAtVehicleInfoPage
    ) {
        return true;
    }

    if (
        isAppraisalValueScreenHidden &&
        isAppraisalAvailable &&
        ((uniqueEstimateOption.hasUniqueOption && currentLocation === routes.TRADE_VEHICLE_INFO) ||
            currentLocation === routes.TRADE_IN_DECISION)
    ) {
        return true;
    }

    return false;
};

export const getSelectedOption = (
    state: any,
    optionsToShow: tradeInMethod[],
    isTradeInValuationProviderGTP: boolean,
    isSalesView: boolean
): SelectedOption => {
    return isSalesView
        ? getSalesViewSelectedOption(state, optionsToShow, isTradeInValuationProviderGTP)
        : getAmdSelectedOption(state, optionsToShow, isTradeInValuationProviderGTP);
};

export const getUniqueEstimateOption = (tradeInSelectors: TradeInAssertionSelectors, isSalesView: boolean): UniqueOption => {
    const optionsToShow = getOptionsToShow(tradeInSelectors, isSalesView);
    return { hasUniqueOption: optionsToShow.length === 1, uniqueOptionValue: optionsToShow[0] };
};

export const updateStepperRouteByName = (stepperRoutes: StepperRoute[], stepName: string, newRoute: string): StepperRoute[] => {
    return stepperRoutes.map((step) => {
        if (step.name === stepName) {
            return {
                ...step,
                route: newRoute
            };
        }
        return step;
    });
};

export const processStepperRoutes = (
    state: any,
    stepperRoutes: StepperRoute[],
    isSalesView: boolean,
    isAmdLienPayoffQuoteEnabled?: boolean,
    tradeIndex?: number,
    updateStepName?: string,
    newRoute?: string
): StepperRoute[] => {
    const tradeInAssertionSelectors = getTradeInSelectors(state, isSalesView);
    const uniqueEstimateOption = getUniqueEstimateOption(tradeInAssertionSelectors, isSalesView);
    const externalTradeIn = isExternalTradeIn(state);
    const externalTradeRoute = hasExternalTradeRoute(state);
    let filteredRoutes = clonedeep(stepperRoutes) as StepperRoute[];
    const optionsToShow: tradeInMethod[] = getOptionsToShow(tradeInAssertionSelectors, isSalesView);
    const { selectedOptionValue }: SelectedOption = getSelectedOption(
        state,
        optionsToShow,
        tradeInAssertionSelectors.isTradeInValuationProviderGTP,
        isSalesView
    );
    const isAppraisalOptionSelected = selectedOptionValue.toUpperCase() === DECISION_TYPES.ENTER_OWN_VALUE.toUpperCase();
    const isAppraisalValueScreenHidden = isSalesView && !tradeInAssertionSelectors.isShowAppraisalValueScreenSalesView;
    // Use tradeIndex to get the correct trade-in's state in multi/single trade-in mode
    const { tradeIn } = getGlobalSelector().getTradeInState(state, tradeIndex);
    const currentLocation = tradeIn.currentUserLocationRoute;

    if (uniqueEstimateOption.hasUniqueOption) {
        filteredRoutes = filteredRoutes.filter((r) => r.route !== routes.TRADE_IN_DECISION);
    }
    if (externalTradeIn) {
        filteredRoutes = filteredRoutes.filter((r) => r.route !== routes.TRADE_IN_DECISION);
        filteredRoutes[0].route = routes.TRADE_IN_EXTERNAL;
    } else if (externalTradeRoute) {
        filteredRoutes[0].route = routes.TRADE_VEHICLE_INFO;
    }
    if (
        isAppraisalValueScreenHidden &&
        (uniqueEstimateOption.uniqueOptionValue === tradeInMethod.MANUAL_ENTRY ||
            (isAppraisalOptionSelected && currentLocation !== routes.TRADE_VEHICLE_INFO))
    ) {
        filteredRoutes = filteredRoutes.filter((r) => r.name !== 'Details');
    }
    if (!isAmdLienPayoffQuoteEnabled) {
        filteredRoutes = filteredRoutes.filter((r) => r.name !== 'Loan Balance');
    }

    // Update specific step route if parameters are provided
    if (updateStepName && newRoute) {
        filteredRoutes = updateStepperRouteByName(filteredRoutes, updateStepName, newRoute);
    }

    return filteredRoutes;
};

export const processStepperRoutesSubaruGTP = (
    state: any,
    stepperRoutes: StepperRoute[],
    isSalesView: boolean,
    isAmdLienPayoffQuoteEnabled?: boolean
): StepperRoute[] => {
    const tradeInAssertionSelectors = getTradeInSelectors(state, isSalesView);
    const uniqueEstimateOption = getUniqueEstimateOption(tradeInAssertionSelectors, isSalesView);
    let updatedRoutes = [...stepperRoutes];

    if (!isAmdLienPayoffQuoteEnabled) {
        updatedRoutes = updatedRoutes.filter((r) => r.route !== routes.TRADE_LOAN_BALANCE);
    }

    const shouldRemoveDecision =
        uniqueEstimateOption.hasUniqueOption || (isSalesView && tradeInAssertionSelectors.isShowAppraisalValueScreenSalesView);

    if (shouldRemoveDecision) {
        updatedRoutes = updatedRoutes.filter((r) => r.route !== routes.TRADE_IN_DECISION);
    }

    return updatedRoutes;
};

export const handleNavigationToManualEntry = (
    isSalesView: boolean,
    isShowAppraisalValueScreenSalesView: boolean,
    dispatch: any
): void => {
    if ((isSalesView && isShowAppraisalValueScreenSalesView) || !isSalesView) {
        dispatch(tradeInComponentActionCreators.updateTradeInCurrentLocation(routes.TRADE_MANUAL_ENTRY));
    }
    if (isSalesView && !isShowAppraisalValueScreenSalesView) {
        const payload = { value: 0, comments: '', source: '' };
        dispatch(tradeInComponentActionCreators.updateTradeValueSource(payload));
        dispatch(tradeInComponentActionCreators.saveOrUpdateTradeIn());
    }
    dispatch(tradeInComponentActionCreators.sendTradeDecisionClickedAnalytics(DECISION_TYPES.ENTER_OWN_VALUE));
};

export const getExternalTradeInConditions = (
    state: any
): { isPartialTrade: boolean; hasAmountOwed: boolean; hasExternalTradeRoute: boolean } => {
    const { tradeIn } = tradeInSelectors.getTradeInState(state);
    const tradeInComponent = tradeIn;
    const { navigation } = state;
    const hasExternalTradeRoute = navigation?.initRedirectLink === routes.TRADE_IN_EXTERNAL;
    if (navigation?.routeIntent !== routes.TRADE_IN_EXTERNAL) {
        return { isPartialTrade: false, hasAmountOwed: false, hasExternalTradeRoute };
    }

    const { amountOwed = 0 } = tradeInComponent || {};

    const hasAmountOwed = amountOwed > 0;

    const result = { isPartialTrade: true, hasAmountOwed, hasExternalTradeRoute };
    return result;
};

export const isExternalTradeIn = (state: ITradeInState): boolean => {
    const externalTradeInConditions = getExternalTradeInConditions(state);
    return externalTradeInConditions.isPartialTrade;
};

export const hasExternalTradeRoute = (state: ITradeInState): boolean => {
    const externalTradeInConditions = getExternalTradeInConditions(state);
    return externalTradeInConditions.hasExternalTradeRoute;
};
